import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { MvpCity, FilterState } from '../types';
import { FilterOption } from '../components/FilterChips';
import { useAuth } from './AuthContext';
import { useUserFavorites } from '../hooks/useUserFavorites';

interface AppContextType {
  selectedCity: MvpCity;
  setSelectedCity: (city: MvpCity) => void;
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  filterOption: FilterOption;
  setFilterOption: (option: FilterOption) => void;
  favorites: string[];
  addFavorite: (saleId: string) => void;
  removeFavorite: (saleId: string) => void;
  isFavorite: (saleId: string) => boolean;
  toggleFavorite: (saleId: string) => void;
  onboardingCompleted: boolean;
  setOnboardingCompleted: (completed: boolean) => void;
  showOnboarding: boolean;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const STORAGE_KEYS = {
  FAVORITES: 'yardapp_favorites',
  ONBOARDING: 'yardapp_onboarding_completed',
};

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const userFavorites = useUserFavorites();
  
  const [selectedCity, setSelectedCity] = useState<MvpCity>('Dallas');
  const [filterOption, setFilterOption] = useState<FilterOption>('all');
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  
  const [filters, setFilters] = useState<FilterState>({
    saleType: 'all',
    dateRange: 'all',
    priceRange: null,
    sourceWebsite: 'all',
  });

  const showOnboarding = 
    import.meta.env.VITE_SHOW_ONBOARDING === 'true' || 
    !onboardingCompleted;

  // Favorites are managed by useUserFavorites hook
  const favorites = userFavorites.favorites;
  const isLoading = userFavorites.isLoading;
  const isSyncing = userFavorites.isSyncing;

  useEffect(() => {
    const storedOnboarding = localStorage.getItem(STORAGE_KEYS.ONBOARDING);
    if (storedOnboarding) {
      setOnboardingCompleted(storedOnboarding === 'true');
    }
  }, []);

  const addFavorite = useCallback((saleId: string) => {
    userFavorites.addFavorite(saleId);
  }, [userFavorites]);

  const removeFavorite = useCallback((saleId: string) => {
    userFavorites.removeFavorite(saleId);
  }, [userFavorites]);

  const isFavorite = useCallback((saleId: string) => {
    return userFavorites.isFavorite(saleId);
  }, [userFavorites]);

  const toggleFavorite = useCallback((saleId: string) => {
    userFavorites.toggleFavorite(saleId);
  }, [userFavorites]);

  const handleOnboardingComplete = useCallback((completed: boolean) => {
    setOnboardingCompleted(completed);
    localStorage.setItem(STORAGE_KEYS.ONBOARDING, String(completed));
  }, []);

  const value: AppContextType = {
    selectedCity,
    setSelectedCity,
    filters,
    setFilters,
    filterOption,
    setFilterOption,
    favorites,
    addFavorite,
    removeFavorite,
    isFavorite,
    toggleFavorite,
    onboardingCompleted: showOnboarding ? false : onboardingCompleted,
    setOnboardingCompleted: handleOnboardingComplete,
    showOnboarding,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
